import { useState } from 'react'
import { X, Truck, AlertCircle } from 'lucide-react'
import api from '@/lib/api'

interface Props {
  isOpen: boolean
  returnId: number
  onClose: () => void
  onSuccess: () => void
}

export default function CarrierClaimModal({ isOpen, returnId, onClose, onSuccess }: Props) {
  const [claimReference, setClaimReference] = useState('')
  const [claimStatus, setClaimStatus] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!claimReference && !claimStatus) {
      setError('Indica al menos la referencia o el estado de la reclamacion')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      await api.post(`/returns/admin/${returnId}/carrier-claim`, {
        claim_reference: claimReference || undefined,
        claim_status: claimStatus || undefined,
        notes: notes || undefined,
      })
      onSuccess()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error actualizando reclamacion')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Reclamacion transportista</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Referencia reclamacion</label>
            <input
              type="text"
              value={claimReference}
              onChange={(e) => setClaimReference(e.target.value)}
              placeholder="Numero de reclamacion"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Estado</label>
            <select
              value={claimStatus}
              onChange={(e) => setClaimStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
            >
              <option value="">Sin cambio</option>
              <option value="open">Abierta</option>
              <option value="resolved">Resuelta</option>
              <option value="denied">Denegada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Notas sobre la reclamacion..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
          >
            {submitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Truck className="w-4 h-4" />
            )}
            {submitting ? 'Guardando...' : 'Actualizar reclamacion'}
          </button>
        </div>
      </div>
    </div>
  )
}
